import { Permission } from '@act/shared/authorization';
import { ActingUserDto, ClientDto } from '@act/shared/data-transfer-objects';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createMigrate,
  MigrationManifest,
  PersistConfig,
  persistReducer,
} from 'redux-persist';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import localStorage from 'redux-persist/lib/storage';
import { userRequests } from 'redux/requests/users.request';
import { LoadingStatus } from 'redux/types/shared-types';
import { createApiThunk, CustomSerializedError } from 'redux/utils/api-thunk';
import { HttpErrorStatus } from 'redux/utils/http-status-codes';
import { useAppSelector } from '../store-hooks';
import { checkPermissionOnUser } from 'redux/utils/auth/permissionChecks';


import { resetRedux } from '../actions/shared-actions';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';

export interface MemberProfileSectionPreferences {
  collapsed: boolean;
}

export interface PerMemberPreferences {
  // Key is the member profile section
  [key: string]: MemberProfileSectionPreferences;
}

export interface UserPreferences {
  [key: string]: unknown;
  memberProfile: {
    perMemberPreferences: {
      // Key is the member id
      [key: string]: PerMemberPreferences;
    };
  };
}

// THUNKS/ACTIONS ==============================================================
export const verifyUser = createApiThunk(userRequests.verifyUser, [
  HttpErrorStatus.UNAUTHORIZED,
  HttpErrorStatus.INTERNAL_SERVER_ERROR,
]);
export const impersonateGuide = createAction<string>('users/impersonateGuide');
export const stopImpersonatingGuide = createAction(
  'users/stopImpersonateGuide'
);

// INITIAL STATE ===============================================================
export interface ActingUserState {
  user: ActingUserDto;
  actualUser: ActingUserDto;
  impersonatedUserId: string;
  status: LoadingStatus;
  isAuthenticated: boolean;
  error: CustomSerializedError;
  preferences: UserPreferences;
  clients: ClientDto[];
}

const initialStatus: ActingUserState = {
  user: null,
  actualUser: null,
  impersonatedUserId: null,
  isAuthenticated: false,
  status: 'initial',
  error: null,
  clients: [],
  preferences: {
    memberProfile: {
      perMemberPreferences: {},
    },
  },
};

// SLICE =======================================================================
export const actingUserSlice = createSlice({
  name: 'actingUser',
  initialState: initialStatus,
  reducers: {
    setActingUser: (
      state: ActingUserState,
      action: PayloadAction<ActingUserDto>
    ) => {
      return { ...state, user: action.payload };
    },
    isAuthenticated: state => {
      return { ...state, isAuthenticated: true };
    },
    updateMemberInfoSectionPreference: (
      state: ActingUserState,
      action: PayloadAction<{
        memberId: string;
        section: string;
        preference: Partial<MemberProfileSectionPreferences>;
      }>
    ) => {
      state.preferences = {
        ...state.preferences,
        memberProfile: {
          ...state.preferences.memberProfile,
          perMemberPreferences: {
            ...state.preferences.memberProfile.perMemberPreferences,
            [action.payload.memberId]: {
              ...state.preferences.memberProfile.perMemberPreferences[
                action.payload.memberId
              ],
              [action.payload.section]: {
                ...state.preferences.memberProfile[action.payload.section],
                ...action.payload.preference,
              },
            },
          },
        },
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, (state, action) => {
        const newState = { ...initialStatus };
        if (action.payload?.preserveImpersonatedUser) {
          newState.impersonatedUserId = state.impersonatedUserId;
          newState.actualUser = state.actualUser;
        } else {
          localStorage.removeItem('impersonatedUserId');
          newState.impersonatedUserId = null;
        }
        return newState;
      })
      .addCase(impersonateGuide, (state, action) => {
        localStorage.setItem('impersonatedUserId', action.payload);
        return { ...state, impersonatedUserId: action.payload };
      })
      .addCase(stopImpersonatingGuide, () => {
        const newState = { ...initialStatus };
        return { ...newState, impersonatedUserId: null };
      })
      .addCase(verifyUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        if (!state.impersonatedUserId) {
          state.actualUser = action.payload.user;
        }
        state.clients = action.payload.clients;
        state.status = 'success';
        state.isAuthenticated = action.payload.authenticated;
        state.user = action.payload.user;
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.error = action.payload;
        state.status = 'failed';
        state.isAuthenticated = false;
        state.user = null;
      });
  },
});

// REDUCER =====================================================================
const migrations = {
  1: (state: ActingUserState): ActingUserState => {
    return {
      ...state,
      preferences: {
        ...state.preferences,
        memberProfile: {
          ...state.preferences.memberProfile,
          perMemberPreferences:
            state.preferences.memberProfile.perMemberPreferences || {},
        },
      },
    };
  },
} as unknown as MigrationManifest;

const persistConfig: PersistConfig<ActingUserState> = {
  key: 'user_preferences',
  storage: localStorage,
  whitelist: ['preferences', 'impersonatedUserId'],
  stateReconciler: autoMergeLevel1,
  version: 1,
  migrate: createMigrate(migrations, { debug: true }),
};
export const actingUserReducer = persistReducer(
  persistConfig,
  actingUserSlice.reducer
);

// SELECTORS ===================================================================
export const { setActingUser, updateMemberInfoSectionPreference } =
  actingUserSlice.actions;

const selectActingUserState = state =>
  (state as { actingUser: ActingUserState })?.actingUser;

export const selectActingUser = state => selectActingUserState(state).user;

const selectLoadingStatus = state => selectActingUserState(state).status;
const selectError = state => selectActingUserState(state).error;
const selectIsAuthenticated = state =>
  selectActingUserState(state).isAuthenticated;

const userHasPermission = (state, permission: Permission) => {
  const user = selectActingUser(state);
  if (!user) return false;
  return checkPermissionOnUser({ actingUser: user, permission });
};

const selectAdministeredClients = state => selectActingUserState(state).clients;

export const selectUserPreferences = state =>
  selectActingUserState(state).preferences;

// EXPORT HELPER ===============================================================
export const actingUserAppSelectors = {
  useSelectActingUserState: () => useAppSelector(selectActingUserState),
  useSelectActingUser: () => useAppSelector(selectActingUser),
  useSelectLoadingStatus: () => useAppSelector(selectLoadingStatus),
  useSelectIsAuthenticated: () => useAppSelector(selectIsAuthenticated),
  useSelectActingUserError: () => useAppSelector(selectError),
  useUserHasPermission: (permission: Permission) =>
    useAppSelector(state => userHasPermission(state, permission)),
  useSelectUserPreferences: () => useAppSelector(selectUserPreferences),
  useSelectAdministeredClients: () => useAppSelector(selectAdministeredClients),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: impersonateGuide,
    effect: async ({ dispatch }) => {
      await dispatch(resetRedux({ preserveImpersonatedUser: true }));
    },
  });
  addEffect({
    action: stopImpersonatingGuide,
    effect: async ({ dispatch }) => {
      await localStorage.removeItem('impersonatedUserId');
      await dispatch(resetRedux({ preserveImpersonatedUser: false }));
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
